import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AnalyticEvents } from '@app/enums';
import { AppDatePipe } from '@app/pipes';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { Translatable } from '@app/types/translatable.type';
import { TimeOffApproverType } from '@models/time-off-v3/time-off-approver-type.model';
import { TimeOffRequestApprovalFlowStep } from '@models/time-off-v3/time-off-request-approval-flow-step.model';
import { TimeOffRequestApproval } from '@models/time-off-v3/time-off-request-approval.model';
import { TimeOffRequest } from '@models/time-off-v3/time-off-request.model';
import { TimeOffCommentTypesEnum } from '@time-off-v3/enum/time-off-comment-types.enum';
import flatten from 'lodash-es/flatten';
import moment from 'moment';

@Component({
    selector: 'app-approval-flows',
    templateUrl: './approval-flows.template.html',
    styleUrls: ['./approval-flows.style.scss'],
})
export class ApprovalFlowsComponent implements OnInit {
    @Input() timeOffRequest = new TimeOffRequest();

    @Output() updating: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() loading: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() reloadRequest: EventEmitter<void> = new EventEmitter<void>();

    isLoading = false;
    // TODO Should be removed once backend permission support this.
    specificPersonApprovalId = 4;
    userIsApprover = false;
    isDenyRequestCommentModalOpen = false;
    appDatePipe = new AppDatePipe();

    constructor(
        public auth: AuthService,
        private notify: NotifyService,
        private analyticService: AnalyticService
    ) {}

    async ngOnInit(): Promise<void> {
        this.loading.emit(true);
        const [timeOffTypes] = await TimeOffApproverType.where('name', 'Specific Person').get();
        this.specificPersonApprovalId = timeOffTypes[0].id;
        this.userIsApprover = flatten(
            this.timeOffRequest.timeOffRequestApprovalFlow.timeOffRequestApprovalFlowSteps.map(
                (flowStep) => flowStep.timeOffRequestApprovals
            )
        ).some((requestApproval: TimeOffRequestApproval) => requestApproval.approver.id === this.auth.employee.id);
        this.loading.emit(false);
    }

    timestampToString(date: Date): string {
        return this.appDatePipe.transform(moment(date), 'longDate') ?? '';
    }

    isViewerTurn(): boolean {
        return this.timeOffRequest.canApproveOrDeny(this.auth.employee);
    }

    isRequestingEmployee(): boolean {
        return this.auth.employee.is(this.timeOffRequest.employee);
    }

    getApproverName(timeOffRequestApprovalFlowStep: TimeOffRequestApprovalFlowStep): string {
        return timeOffRequestApprovalFlowStep.timeOffRequestApprovals.find(
            (requestApproval) => requestApproval.hasApproved !== null
        ).approver?.fullName;
    }

    getSpecificPersonApproverName(timeOffRequestApprovalFlowStep: TimeOffRequestApprovalFlowStep): Translatable {
        const approver = timeOffRequestApprovalFlowStep.timeOffRequestApprovals[0].approver;
        if (!approver) {
            return 'time-off-v3.removedUser';
        }

        const isTerminated = moment(approver.terminatedOn).isBefore(moment());

        return isTerminated
            ? { key: 'time-off-v3.terminatedUser', params: { name: approver.fullName } }
            : approver.fullName;
    }

    getApprovalTimestamp(timeOffRequestApprovalFlowStep: TimeOffRequestApprovalFlowStep): string {
        return this.timestampToString(
            timeOffRequestApprovalFlowStep.timeOffRequestApprovals.find(
                (requestApproval) => requestApproval.hasApproved !== null
            ).updatedAt
        );
    }

    async onApprove(): Promise<void> {
        this.updating.emit(true);
        await this.findTimeOffRequestApproval().approve();
        this.reloadRequest.emit();
        this.notify.success('time-off-v3.timeOffRequestHasBeenApproved');
        this.analyticService.trackEvent(AnalyticEvents.TimeOffManageRequestClickApprove);
        this.updating.emit(false);
    }

    onCloseDenyRequestModal(): void {
        this.isDenyRequestCommentModalOpen = false;
        return;
    }

    async onConfirmDenyRequest(comment?: string): Promise<void> {
        this.updating.emit(true);

        try {
            const timeOffRequestApproval = this.findTimeOffRequestApproval();

            timeOffRequestApproval.type = TimeOffCommentTypesEnum.DENIED;
            timeOffRequestApproval.comment = comment;

            await timeOffRequestApproval.deny();

            this.reloadRequest.emit();
            this.notify.success('time-off-v3.timeOffRequestHasBeenDenied');
            this.analyticService.trackEvent(AnalyticEvents.TimeOffManageRequestClickDeny);

            if (comment) {
                this.analyticService.trackEvent(
                    AnalyticEvents.TimeOffManageRequestMeatballDenyAddCommentClickDenyRequestToSave
                );
            }
        } catch (e) {
            this.notify.error(e.message);
        }

        this.updating.emit(false);
        this.isDenyRequestCommentModalOpen = false;
    }

    onDeny(): void {
        this.isDenyRequestCommentModalOpen = true;
    }

    trackLearnMoreBackupApprover(): void {
        this.analyticService.trackEvent(AnalyticEvents.TimeOffManageARequestApprovalFlowLink);
    }

    trackCreateNewRequest(): void {
        this.analyticService.trackEvent(AnalyticEvents.TimeOffManageRequestClickCreateNewRequest);
    }

    private findTimeOffRequestApproval(): TimeOffRequestApproval {
        return this.timeOffRequest.timeOffRequestApprovalFlow.timeOffRequestApprovalFlowSteps
            .find((requestApprovalFlowStep) =>
                requestApprovalFlowStep.timeOffRequestApprovals
                    .map((requestApproval) => requestApproval.approver.id)
                    .includes(this.auth.employee.id)
            )
            .timeOffRequestApprovals.find((requestApproval) => requestApproval.approver.is(this.auth.employee));
    }
}
